import { mediaQuery } from '@cvpartner/design-system/vars';
import type React from 'react';
import {
  Breadcrumb as AriaBreadcrumb,
  Breadcrumbs as AriaBreadcrumbs,
} from 'react-aria-components';
import { useMediaLayout } from 'use-media';
import * as styles from './Breadcrumbs.css';
import { Link } from './Link';

export interface Breadcrumb {
  label: string;
  component?: React.ReactNode;
  href?: string;
}

interface BreadcrumbProps {
  breadcrumbs_tree: readonly Breadcrumb[];
  children?: React.ReactNode;
}

const BreadcrumbItem: React.FC<{
  breadcrumb: Breadcrumb;
  isLast: boolean;
}> = ({ breadcrumb: { component, href, label }, isLast }) => {
  return (
    <AriaBreadcrumb className={styles.breadcrumb}>
      {component ? (
        component
      ) : href ? (
        <Link className={styles.breadcrumbLink} to={href}>
          {label}
        </Link>
      ) : (
        <span className={styles.breadcrumbText}>{label}</span>
      )}
      {!isLast && <span className={styles.slash}>/</span>}
    </AriaBreadcrumb>
  );
};

function useBreadcrumbsForScreenSize(
  breadcrumbs_tree: readonly Breadcrumb[],
): readonly Breadcrumb[] {
  const isLargeScreen = useMediaLayout(mediaQuery.xxs, true);

  if (isLargeScreen || breadcrumbs_tree.length < 3) {
    return breadcrumbs_tree;
  }

  return [{ label: '…' }, ...breadcrumbs_tree.slice(-2)];
}

export const Breadcrumbs: React.FC<BreadcrumbProps> = ({
  breadcrumbs_tree,
  children,
}) => {
  const breadcrumbs = useBreadcrumbsForScreenSize(breadcrumbs_tree);

  return (
    <div className={styles.breadcrumbsContainer}>
      <AriaBreadcrumbs className={styles.breadcrumbs}>
        {breadcrumbs.map((breadcrumb, index) => (
          <BreadcrumbItem
            key={breadcrumb.label}
            breadcrumb={breadcrumb}
            isLast={index === breadcrumbs.length - 1}
          />
        ))}
      </AriaBreadcrumbs>
      {children != null && (
        <div className={styles.breadcrumbsChildren}>{children}</div>
      )}
    </div>
  );
};
